import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Exercise } from '../types/exercise';
import { exerciseService } from '../services/exerciseService';
import { pdfService } from '../services/pdfService';
// import { powerPointService } from '../services/powerPointService';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Download, Edit, Trash2, Calendar, Clock, MapPin, Users, FileText, Presentation } from 'lucide-react';

export const ExerciseView: React.FC = () => {
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingPPT, setExportingPPT] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadExercise();
  }, [id, user]);

  const loadExercise = async () => {
    if (!user || !id) return;
    
    try {
      const exercises = await exerciseService.getUserExercises(user.uid);
      const foundExercise = exercises.find(ex => ex.id === id);
      setExercise(foundExercise || null);
    } catch (error) {
      console.error('Error loading exercise:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!exercise) return;
    
    try {
      setExportingPDF(true);
      await pdfService.generateExercisePDF(exercise);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportPPT = async () => {
    if (!exercise) return;
    
    try {
      setExportingPPT(true);
      // PowerPoint export temporarily disabled - will re-enable after app is working
      alert('PowerPoint export will be available after we get the app running!');
    } catch (error) {
      console.error('Error exporting PowerPoint:', error);
      alert('Failed to export PowerPoint. Please try again.');
    } finally {
      setExportingPPT(false);
    }
  };

  const handleDelete = async () => {
    if (!exercise) return;
    
    const confirmed = window.confirm('Are you sure you want to delete this exercise? This action cannot be undone.');
    if (!confirmed) return;
    
    try {
      await exerciseService.deleteExercise(exercise.id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting exercise:', error);
      alert('Failed to delete exercise. Please try again.');
    }
  };

  const getStatusBadgeColor = (status: Exercise['status']) => {
    switch (status) {
      case 'Planned':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading exercise...</div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Exercise not found</h3>
              <p className="mt-2 text-gray-600">The exercise you're looking for doesn't exist or you don't have access to it.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Button variant="outline" onClick={() => navigate('/')} className="mr-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{exercise.title}</h1>
                <p className="text-gray-600">Exercise Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(exercise.status)}`}>
                {exercise.status}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mb-6">
          <Button variant="outline" onClick={() => navigate(`/exercises/${exercise.id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Exercise
          </Button>
          <Button onClick={handleExportPDF} disabled={exportingPDF}>
            <Download className="w-4 h-4 mr-2" />
            {exportingPDF ? 'Exporting PDF...' : 'Export PDF'}
          </Button>
          <Button onClick={handleExportPPT} disabled={exportingPPT} variant="outline">
            <Presentation className="w-4 h-4 mr-2" />
            {exportingPPT ? 'Exporting PPT...' : 'Export PowerPoint'}
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Exercise Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">{formatDate(exercise.exerciseDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium">{exercise.exerciseTime}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium">{exercise.location || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Scenario Type</p>
                    <p className="font-medium">{exercise.scenarioType}</p>
                  </div>
                </div>
              </div>
              
              {exercise.description && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{exercise.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Learning Objectives */}
          <Card>
            <CardHeader>
              <CardTitle>Learning Objectives</CardTitle>
              <CardDescription>{exercise.objectives.length} objective(s) defined</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {exercise.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{objective}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Exercise Injects */}
          {exercise.injects.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Exercise Injects</CardTitle>
                <CardDescription>{exercise.injects.length} inject(s) scheduled</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {exercise.injects
                    .sort((a, b) => a.timeOffsetMinutes - b.timeOffsetMinutes)
                    .map((inject, index) => (
                    <div key={inject.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-600">
                          T+{inject.timeOffsetMinutes} minutes
                        </span>
                        <span className="text-xs text-gray-500">Inject {index + 1}</span>
                      </div>
                      <p className="text-gray-700">{inject.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {exercise.regulatoryTriggers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Regulatory Compliance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {exercise.regulatoryTriggers.map((trigger, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-sm"
                      >
                        {trigger}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {exercise.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Additional Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-wrap">{exercise.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Exercise Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Created by:</span> {exercise.createdByEmail}
                </div>
                <div>
                  <span className="font-medium">Created:</span> {exercise.createdAt.toDate().toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Last updated:</span> {exercise.updatedAt.toDate().toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Exercise ID:</span> {exercise.id}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
